/**
 * UnattributedBanner
 *
 * Surfaces LLM cost that's been tracked without a user_id (sentinel UUID
 * 00000000-0000-0000-0000-000000000000). When `_auto_track_usage` in
 * services/llm.py can't resolve a user_id, it tracks the call against
 * this sentinel instead of silently dropping it. A healthy system has
 * <2% unattributed spend (legitimate system calls). Higher means a
 * call site is missing user_id and needs to be sewn up.
 *
 * Severity:
 *   - critical (≥20%): red banner — major tracking gap, investigate immediately
 *   - warning  (≥5%):  yellow banner — measurable gap, find and fix
 *   - info     (≥1%):  light info banner — minor, mostly system calls
 *   - ok       (<1%):  no banner shown
 *
 * Used on: /admin/llm-usage and /admin/subscription-analytics
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { storage } from '../../services/storage';
import { API_BASE_URL } from '../../utils/apiConfig';

interface UnattributedCallName {
  call_name: string;
  model_used: string;
  calls: number;
  cost: number;
  avg_cost: number;
}

interface UnattributedSummary {
  period_days: number;
  totals: {
    total_calls: number;
    total_cost: number;
    total_input_tokens: number;
    total_output_tokens: number;
    period_total_cost: number;
    pct_of_period: number;
    severity: 'ok' | 'info' | 'warning' | 'critical';
  };
  by_call_name: UnattributedCallName[];
  daily_trend: { day: string; calls: number; cost: number }[];
}

interface Props {
  /** Number of days to look back. Defaults to 7. */
  days?: number;
}

const SEVERITY_STYLES: Record<string, { bg: string; border: string; text: string; icon: React.ReactNode; label: string }> = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-900',
    icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
    label: 'Critical tracking gap',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-900',
    icon: <AlertCircle className="h-5 w-5 text-amber-600" />,
    label: 'Tracking gap detected',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-900',
    icon: <Info className="h-5 w-5 text-blue-600" />,
    label: 'Minor unattributed calls',
  },
};

export const UnattributedBanner: React.FC<Props> = ({ days = 7 }) => {
  const [data, setData] = useState<UnattributedSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await storage.getItem('admin_token');
        if (!token) return;
        const res = await fetch(
          `${API_BASE_URL}/api/admin/subscription-analytics/unattributed-summary?days=${days}`,
          {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            credentials: 'include',
          }
        );
        if (!res.ok) return;
        const json = (await res.json()) as UnattributedSummary;
        if (!cancelled) setData(json);
      } catch (err) {
        // Silent — banner is non-critical UI
        console.warn('UnattributedBanner: failed to load', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (loading || !data) return null;

  const sev = data.totals.severity;
  // Don't render if everything is healthy
  if (sev === 'ok') return null;

  const style = SEVERITY_STYLES[sev];
  const top3 = data.by_call_name.slice(0, 3);
  const totalCallNames = data.by_call_name.length;

  return (
    <div className={`${style.bg} ${style.border} border rounded-lg p-4 mb-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{style.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className={`font-semibold ${style.text}`}>
              {style.label}: ${data.totals.total_cost.toFixed(2)} ({data.totals.pct_of_period.toFixed(1)}% of LLM spend)
              <span className="font-normal opacity-75 ml-1">
                over the last {data.period_days} day{data.period_days !== 1 ? 's' : ''}
              </span>
            </p>
            {totalCallNames > 0 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className={`text-xs font-medium underline ${style.text} hover:opacity-75 flex items-center gap-1`}
              >
                {expanded ? 'Hide details' : 'View details'}
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            )}
          </div>

          <p className={`text-sm mt-1 ${style.text} opacity-90`}>
            These calls fired without a user_id, so per-user cost metrics exclude them.
            {top3.length > 0 && (
              <> Top sources: <span className="font-mono text-xs">{top3.map(c => c.call_name).join(', ')}</span></>
            )}
          </p>

          {expanded && data.by_call_name.length > 0 && (
            <div className="mt-3 bg-white bg-opacity-50 rounded border border-current border-opacity-20 overflow-hidden">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className={`${style.text} border-b border-current border-opacity-20`}>
                    <th className="px-3 py-2 text-left font-medium">Call Name</th>
                    <th className="px-3 py-2 text-left font-medium">Model</th>
                    <th className="px-3 py-2 text-right font-medium">Calls</th>
                    <th className="px-3 py-2 text-right font-medium">Total Cost</th>
                    <th className="px-3 py-2 text-right font-medium">Avg Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_call_name.map((c, i) => (
                    <tr key={`${c.call_name}-${c.model_used}-${i}`} className="border-t border-current border-opacity-10">
                      <td className="px-3 py-1.5 font-mono">{c.call_name}</td>
                      <td className="px-3 py-1.5 text-gray-600">{c.model_used}</td>
                      <td className="px-3 py-1.5 text-right">{c.calls.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right font-medium">${c.cost.toFixed(4)}</td>
                      <td className="px-3 py-1.5 text-right">${c.avg_cost.toFixed(6)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className={`px-3 py-2 text-xs ${style.text} opacity-75 italic border-t border-current border-opacity-20`}>
                Action: find each call_name in the codebase and add{' '}
                <code className="font-mono bg-white bg-opacity-50 px-1 rounded">user_id=user_id</code>{' '}
                to its <code className="font-mono bg-white bg-opacity-50 px-1 rounded">call_llm()</code> invocation.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
